import React, {useCallback, useEffect, useState} from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grow,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    TextField,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import {useSnackbar} from 'notistack';
import API_Service, {axiosURIencode} from '../../../../Services/API_Service';
import {EmptyState, SectionToolbar, SettingsSurface, StatusSwitch, StatusTabs} from './ui';

const useStyles = makeStyles((theme) => ({
    addButton: {
        width: 40,
        height: 40,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 7px 16px rgba(0,0,0,.16)',
        transition: 'transform 140ms cubic-bezier(.34,1.3,.64,1), box-shadow 140ms ease',
        '&:hover': {backgroundColor: theme.palette.primary.main, transform: 'translateY(-2px) scale(1.04)', boxShadow: '0 10px 22px rgba(0,0,0,.2)'},
        '&:active': {transform: 'scale(.96)'}
    },
    listItem: {
        minHeight: 58,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(12),
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: 'background-color 120ms ease, transform 120ms ease',
        animation: '$rowIn 240ms cubic-bezier(0,0.4,0,1) both',
        '&:last-child': {borderBottom: 0},
        '&:hover': {backgroundColor: theme.palette.action.hover, transform: 'translateX(2px)'}
    },
    inactive: {opacity: 0.58},
    rowName: {fontWeight: 650},
    dialogPaper: {borderRadius: 16},
    dialogTitle: {paddingBottom: theme.spacing(1), fontWeight: 800},
    dialogContent: {paddingTop: `${theme.spacing(1)}px !important`},
    stagger0: {animationDelay: '0ms'},
    stagger1: {animationDelay: '45ms'},
    stagger2: {animationDelay: '90ms'},
    stagger3: {animationDelay: '135ms'},
    stagger4: {animationDelay: '180ms'},
    stagger5: {animationDelay: '225ms'},
    stagger6: {animationDelay: '270ms'},
    stagger7: {animationDelay: '315ms'},
    '@keyframes rowIn': {from: {opacity: 0, transform: 'translateY(6px)'}, to: {opacity: 1, transform: 'translateY(0)'}}
}));

export default function ManagedListSection({
    axiosConfig,
    axiosPut,
    language,
    endpoint,
    statusEndpoint,
    idField,
    nameField,
    statusField,
    dialogTitle,
    fieldLabel,
    validationMessage
}) {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const {enqueueSnackbar} = useSnackbar();
    const [items, setItems] = useState(null);
    const [statusFilter, setStatusFilter] = useState(1);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setItems(null);
        API_Service.get(endpoint, axiosConfig).then(({data}) => {
            if (parseInt(data.result) === 1) setItems(data.extraData || []);
            else {
                setItems([]);
                enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
            }
        }).catch(() => {
            setItems([]);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    }, [axiosConfig, endpoint, enqueueSnackbar, language.operation_fail]);

    useEffect(() => { load(); }, [load]);

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
        setEditId(null);
        setValue('');
    };

    const openAdd = () => {
        setEditId(null);
        setValue('');
        setDialogOpen(true);
    };

    const openEdit = (item) => {
        setEditId(item[idField]);
        setValue(item[nameField] || '');
        setDialogOpen(true);
    };

    const save = () => {
        const trimmed = value.trim();
        if (!trimmed) {
            enqueueSnackbar(validationMessage || language.name_required || fieldLabel, {variant: 'warning'});
            return;
        }
        if (saving) return;
        setSaving(true);
        const request = editId
            ? API_Service.put(endpoint, axiosURIencode({[idField]: editId, [nameField]: trimmed}), axiosPut)
            : (() => {
                const formdata = new FormData();
                formdata.append(nameField, trimmed);
                return API_Service.post(endpoint, formdata, axiosConfig);
            })();
        request.then(({data}) => {
            setSaving(false);
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                setDialogOpen(false);
                setEditId(null);
                setValue('');
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => {
            setSaving(false);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    };

    const [togglingId, setTogglingId] = useState(null);

    const toggleStatus = (item) => {
        setTogglingId(item[idField]);
        API_Service.put(statusEndpoint, axiosURIencode({[idField]: item[idField]}), axiosPut).then(({data}) => {
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'}))
            .finally(() => setTogglingId(null));
    };

    const filtered = (items || [])
        .filter((item) => parseInt(item[statusField]) === parseInt(statusFilter))
        .filter((item) => !search || String(item[nameField] || '').toLowerCase().includes(search.toLowerCase()));

    return (
        <React.Fragment>
            <SectionToolbar search={search} onSearch={setSearch} placeholder={language.search}>
                <StatusTabs
                    value={statusFilter}
                    onChange={setStatusFilter}
                    activeLabel={language.active}
                    inactiveLabel={language.inactive}
                    ariaLabel={language.status || 'Status'}
                />
                <Tooltip title={language.add}>
                    <IconButton className={classes.addButton} onClick={openAdd} aria-label={language.add}><AddIcon/></IconButton>
                </Tooltip>
            </SectionToolbar>
            <SettingsSurface>
                {items === null ? (
                    <div>{[0, 1, 2, 3].map((row) => <Skeleton key={row} animation="wave" height={58}/>)}</div>
                ) : filtered.length === 0 ? <EmptyState label={language.no_data}/> : (
                    <List disablePadding>
                        {filtered.map((item, index) => {
                            const active = parseInt(item[statusField]) === 1;
                            return (
                                <ListItem
                                    key={item[idField]}
                                    className={`${classes.listItem} ${classes[`stagger${Math.min(index, 7)}`]} ${active ? '' : classes.inactive}`}
                                >
                                    <ListItemText primary={item[nameField]} primaryTypographyProps={{className: classes.rowName}}/>
                                    <ListItemSecondaryAction>
                                        <Tooltip title={language.edit}>
                                            <IconButton size="small" onClick={() => openEdit(item)} aria-label={`${language.edit} ${item[nameField]}`}><EditIcon fontSize="small"/></IconButton>
                                        </Tooltip>
                                        <StatusSwitch active={active} pending={String(togglingId) === String(item[idField])} name={item[nameField]} language={language} onToggle={() => toggleStatus(item)}/>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </SettingsSurface>
            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="sm"
                TransitionComponent={Grow}
                PaperProps={{className: classes.dialogPaper}}
            >
                <DialogTitle className={classes.dialogTitle}>{editId ? language.edit : dialogTitle}</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <TextField
                        id="managed-list-name"
                        autoFocus
                        fullWidth
                        variant="outlined"
                        label={fieldLabel}
                        value={value}
                        onChange={(event) => setValue(event.target.value)}
                        onKeyPress={(event) => { if (event.key === 'Enter') save(); }}
                        inputProps={{maxLength: 255}}
                    />
                </DialogContent>
                <DialogActions>
                    <Button disabled={saving} onClick={closeDialog}>{language.cancel}</Button>
                    <Button variant="contained" color="primary" disabled={saving || !value.trim()} onClick={save}>
                        {saving ? <CircularProgress size={18}/> : editId ? language.update : language.add}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
